import { GetStaticProps } from 'next';
import { groq } from 'next-sanity';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { SecondaryButtonMedium } from '@/components/base/Buttons';
import { SecondaryCard } from '@/components/base/Cards';
import { PageContainer, PageSectionLarge, PageBackgroundFull } from '@/components/base/Sections';
import {
  TypographySectionTitle,
  TypographyText,
  TypographyTitle,
  TypographyHero,
} from '@/components/base/Typography';
import { sanityClient } from '@/config/sanity';

const Page = ({ articles }: { articles: any[] }) => {
  const seoBase = {
    title: 'Education Centre',
    description:
      'Learn about diamonds and how to find your perfect diamond with our education centre.',
  };

  return (
    <>
      <NextSeo {...seoBase} />
      <PageContainer className="space-y-8">
        <PageBackgroundFull className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-slate-900/60 to-slate-900" />
          </div>
          <PageSectionLarge className="relative z-10 py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-4 text-center">
              <div className="mx-auto max-w-3xl">
                <TypographyHero className="mb-4 text-white">Education Centre</TypographyHero>
                <div className="mx-auto my-6 h-0.5 w-24 bg-white/30" aria-hidden />
                <TypographyText className="text-lg leading-relaxed text-white/90 sm:text-xl">
                  Learn about diamonds and how to find your perfect diamond with our education
                  centre.
                </TypographyText>

                <div className="mt-8 flex justify-center">
                  <SecondaryButtonMedium>
                    <Link href="/education/caratGuide">Read the Carat Guide</Link>
                  </SecondaryButtonMedium>
                </div>
              </div>
            </div>
          </PageSectionLarge>
        </PageBackgroundFull>

        <PageSectionLarge className="space-y-8">
          <TypographyTitle className="">Browse Articles</TypographyTitle>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <SecondaryCard key={article._id} className="overflow-hidden p-0">
                <Link
                  href={`/education/${article.slug.current}`}
                  className="flex h-full flex-col space-y-4"
                >
                  {article.mainImage?.asset?.url && (
                    <img
                      src={article.mainImage.asset.url}
                      alt={article.title}
                      className="h-64 w-full object-cover"
                    />
                  )}
                  <div className="flex flex-1 flex-col space-y-2 p-8">
                    <TypographySectionTitle className="line-clamp-2 text-lg">
                      {article.title}
                    </TypographySectionTitle>
                    {article.author?.name && (
                      <TypographyText className="text-muted-foreground mt-auto line-clamp-3 text-sm">
                        By {article.author.name}
                      </TypographyText>
                    )}
                  </div>
                </Link>
              </SecondaryCard>
            ))}
          </div>
        </PageSectionLarge>
      </PageContainer>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const query = groq`
    *[_type == "post"] | order(publishedAt desc) {
      _id,
      title,
      "slug": slug { current },
      mainImage {
        asset->{
          url
        }
      },
      author->{ name },
      categories[]->{title},
      publishedAt
    }
  `;

  const articles = await sanityClient.fetch(query);
  return {
    props: { articles },
    revalidate: 60,
  };
};

export default Page;
